import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import { VerificationDialog } from '../../components/dialogs';
import Toast from '../../components/Toast';
import { CheckCircle, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import Captcha, { validateCaptcha } from '../../components/Captcha';
import { obfuscateEmail } from '../../utils/emailObfuscation';

const VigilanceSignup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Registration data
  const [registrationData, setRegistrationData] = useState({
    fullname: '',
    email: '',
    mobile: '',
    employee_id: ''
  });

  // Employment status
  const [isEmployed, setIsEmployed] = useState(false);
  const [employmentId, setEmploymentId] = useState('');
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);
  const [isEmployeeVerified, setIsEmployeeVerified] = useState(false);

  // Verification state
  const [verificationState, setVerificationState] = useState({
    emailVerified: false,
    phoneVerified: false,
    emailOtpSent: false,
    phoneOtpSent: false,
    otpToken: null
  });

  // Dialog state
  const [verificationDialog, setVerificationDialog] = useState({
    isOpen: false,
    type: '', // 'email' or 'phone'
    login: ''
  });

  // Toast state
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // CAPTCHA state
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [refreshTriggerStep1, setRefreshTriggerStep1] = useState(0);
  const [captchaInputStep2, setCaptchaInputStep2] = useState('');
  const [captchaKeyStep2, setCaptchaKeyStep2] = useState('');
  const [refreshTriggerStep2, setRefreshTriggerStep2] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (isEmployed && !employmentId.trim()) {
      newErrors.employmentId = 'Employment ID is required';
    }

    if (!registrationData.fullname.trim()) {
      newErrors.fullname = 'Full name is required';
    }

    if (!registrationData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(registrationData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!registrationData.mobile.trim()) {
      newErrors.mobile = 'Phone number is required';
    } else {
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(registrationData.mobile)) {
        newErrors.mobile = 'Please enter a valid 10-digit phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Submit = async () => {
    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInput);
    if (!captchaValidation.isValid) {
      setErrors(prev => ({
        ...prev,
        captcha: captchaValidation.error
      }));
      return;
    }

    if (!validateStep1()) return;

    // If employed and still loading employee data, wait a bit
    if (isEmployed && employmentId.trim() && isLoadingEmployee) {
      setErrors({ general: 'Please wait while employee details are being fetched...' });
      return;
    }

    setIsSubmitting(true);
    try {
      // Call registration API
      const result = await authService.registerUser({
        ...registrationData,
        captcha: captchaInput,
        captcha_key: captchaKey
      });

      if (result.success) {
        setCurrentStep(2);
        // Reset CAPTCHA after successful registration
        setCaptchaInput('');
      } else {
        setErrors({ general: result.error || result.message || 'Registration failed. Please try again.' });
        setRefreshTriggerStep1(prev => prev + 1);
        setCaptchaInput('');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'Registration failed. Please try again.' });
      setRefreshTriggerStep1(prev => prev + 1);
      setCaptchaInput('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendOtp = async (type) => {
    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInputStep2);
    if (!captchaValidation.isValid) {
      setErrors({ general: captchaValidation.error });
      return;
    }

    setIsSubmitting(true);
    try {
      const login = type === 'email' ? registrationData.email : registrationData.mobile;

      const result = await authService.sendVerificationOtp({
        login,
        captcha: captchaInputStep2,
        captcha_key: captchaKeyStep2
      });

      // CAPTCHA was consumed in this attempt, refresh step 2 captcha
      setRefreshTriggerStep2(prev => prev + 1);
      setCaptchaInputStep2('');

      // 🔐 SECURITY: Check for otp_token in response, not just success flag
      // This prevents Burp Suite from faking 200 OK without the actual token
      if (result.success && result.data?.otp_token) {
        setVerificationState(prev => ({
          ...prev,
          [`${type}OtpSent`]: true,
          otpToken: result.data.otp_token // Store the OTP token
        }));

        // Open verification dialog
        setVerificationDialog({
          isOpen: true,
          type: type,
          login: login
        });
      } else if (result.success && !result.data?.otp_token) {
        // Security error: Got success but no token (possible manipulation)
        const errorMessage = 'Security Error: Invalid server response. Please try again.';
        setErrors({ [`${type}Verification`]: errorMessage });
      } else {
        const errorMessage = result.error || result.message || 'Failed to send OTP';
        setErrors({ general: errorMessage });
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setErrors({ general: 'Failed to send OTP. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (otp, captcha, captchaKey) => {
    setIsSubmitting(true);
    try {
      const result = await authService.verifyVerificationOtp({
        login: verificationDialog.login,
        otp: otp,
        captcha: captcha,
        captcha_key: captchaKey,
        otp_token: verificationState.otpToken // Send the stored OTP token
      });

      if (result.success) {
        // Mark as verified
        setVerificationState(prev => ({
          ...prev,
          [`${verificationDialog.type}Verified`]: true
        }));

        // Close dialog
        setVerificationDialog({ isOpen: false, type: '', login: '' });

        // Check if both are verified, then proceed to login
        // Explicitly copy all properties to ensure proper state checking
        const newState = {
          emailVerified: verificationState.emailVerified,
          phoneVerified: verificationState.phoneVerified,
          emailOtpSent: verificationState.emailOtpSent,
          phoneOtpSent: verificationState.phoneOtpSent,
          otpToken: verificationState.otpToken,
          [`${verificationDialog.type}Verified`]: true
        };

        if (newState.emailVerified && newState.phoneVerified) {
          // Both verified, proceed to login
          handleCompleteSignup();
        }
      } else {
        setErrors({ general: result.error || result.message || 'OTP verification failed. Please resend OTP.' });
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setErrors({ general: 'OTP verification failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteSignup = () => {
    // Show success toast
    setToast({
      isVisible: true,
      message: 'Account verified successfully! Please login to continue.',
      type: 'success'
    });

    // Redirect to login page after a short delay
    setTimeout(() => {
      navigate('/vigilance-login');
    }, 2000);
  };

  const handleVerifyEmployee = async () => {
    // Validate CAPTCHA
    const captchaValidation = validateCaptcha(captchaInput);
    if (!captchaValidation.isValid) {
      setErrors(prev => ({ ...prev, captcha: captchaValidation.error }));
      return;
    }

    if (!employmentId.trim()) {
      setErrors(prev => ({ ...prev, employmentId: 'Employment ID is required' }));
      return;
    }

    setIsLoadingEmployee(true);
    setErrors({}); // Clear errors

    try {
      const employeeResult = await authService.getEmployeeDetail(
        employmentId.trim(),
        captchaInput,
        captchaKey
      );

      if (employeeResult.success && employeeResult.data) {
        const employeeData = employeeResult.data;

        // Auto-fill form fields from employee data
        setRegistrationData(prev => ({
          ...prev,
          fullname: employeeData.name || employeeData.fullname || prev.fullname,
          email: employeeData.email || prev.email,
          mobile: employeeData.mobile || employeeData.mobile_number || employeeData.phone || prev.mobile,
          employee_id: employmentId.trim()
        }));

        setIsEmployeeVerified(true);
        setRefreshTriggerStep1(prev => prev + 1);
        setCaptchaInput(''); // Clear CAPTCHA after verification so it must be solved again for submit

        // Clear any errors
        setErrors({});
      } else {
        // Show error message on verification failure
        setErrors({ general: employeeResult.error || employeeResult.message || 'Employee verification failed. Please try again.' });
        // Silent rejection - revert to normal state
        setIsEmployed(false);
        setEmploymentId('');
        setRefreshTriggerStep1(prev => prev + 1);
        setCaptchaInput('');
        setIsEmployeeVerified(false);
      }
    } catch (error) {
      console.error('Error fetching employee details:', error);
      setErrors({ general: 'Failed to fetch employee details. Please try again.' });
      // Silent rejection - revert to normal state
      setIsEmployed(false);
      setEmploymentId('');
      setRefreshTriggerStep1(prev => prev + 1);
      setCaptchaInput('');
      setIsEmployeeVerified(false);
    } finally {
      setIsLoadingEmployee(false);
    }
  };

  const renderStep1 = () => {
    const showMiniForm = isEmployed && !isEmployeeVerified;

    return (
      <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); showMiniForm ? handleVerifyEmployee() : handleStep1Submit(); }} className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Basic Information</h3>
          <p className="text-gray-600">Enter your details to get started</p>
        </div>

        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{errors.general}</p>
          </div>
        )}

        {/* Employed Checkbox */}
        <div className="flex items-center">
          <input
            id="isEmployed"
            type="checkbox"
            checked={isEmployed}
            onChange={(e) => {
              setIsEmployed(e.target.checked);
              setRefreshTriggerStep1(prev => prev + 1);
              setCaptchaInput('');
              if (!e.target.checked) {
                setEmploymentId('');
                setIsEmployeeVerified(false);
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.employmentId;
                  return newErrors;
                });
              }
            }}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="isEmployed" className="ml-2 text-sm font-medium text-gray-700">
            I am an employee
          </label>
        </div>

        {/* Employment ID */}
        {isEmployed && (
          <div>
            <label htmlFor="employmentId" className="block text-sm font-medium text-gray-700 mb-2">
              Employment ID *
            </label>
            <div className="relative">
              <input
                id="employmentId"
                type="text"
                autoComplete="off"
                value={employmentId}
                readOnly={isEmployeeVerified}
                onChange={(e) => {
                  setEmploymentId(e.target.value);
                  if (errors.employmentId) {
                    setErrors(prev => ({ ...prev, employmentId: '' }));
                  }
                }}
                className={`w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.employmentId ? 'border-red-500' : 'border-gray-300'
                  } ${isEmployeeVerified ? 'bg-gray-100 text-gray-500' : ''}`}
                placeholder="Enter your employment ID"
              />
              {isLoadingEmployee && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
                </div>
              )}
            </div>
            {errors.employmentId && <p className="mt-1 text-sm text-red-600">{errors.employmentId}</p>}
          </div>
        )}

        {!showMiniForm && (
          <>
            {/* Full Name */}
            <div>
              <label htmlFor="fullname" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                id="fullname"
                name="fullname"
                type="text"
                autoComplete="off"
                value={registrationData.fullname}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.fullname ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter your full name"
              />
              {errors.fullname && <p className="mt-1 text-sm text-red-600">{errors.fullname}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="off"
                value={registrationData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter your email address"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            {/* Mobile */}
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                autoComplete="off"
                value={registrationData.mobile}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.mobile ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter your phone number"
                maxLength="10"
              />
              {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
            </div>
          </>
        )}

        {/* CAPTCHA - Show in both states but potentially remounts/different context usage */}
        <Captcha
          value={captchaInput}
          onChange={(e) => {
            setCaptchaInput(e.target.value);
            if (errors.captcha) {
              setErrors(prev => ({ ...prev, captcha: '' }));
            }
          }}
          error={errors.captcha}
          disabled={isSubmitting || isLoadingEmployee}
          onCaptchaChange={(data) => setCaptchaKey(data.key)}
          refreshTrigger={refreshTriggerStep1}
        />

        {showMiniForm ? (
          <button
            type="button"
            onClick={handleVerifyEmployee}
            disabled={isLoadingEmployee}
            className={`w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold transition-colors duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center space-x-2 ${isLoadingEmployee ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-700'
              }`}
          >
            {isLoadingEmployee ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Verify Employee</span>}
            {!isLoadingEmployee && <ArrowRight className="w-4 h-4" />}
          </button>
        ) : (
          <button
            onClick={handleStep1Submit}
            disabled={isSubmitting}
            className={`w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold transition-colors duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center space-x-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-700'
              }`}
          >
            <span>{isSubmitting ? 'Registering...' : 'Continue'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </form>
    );
  };

  const renderStep2 = () => (
    <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); }} className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Verify Your Contact</h3>
        <p className="text-gray-600">Verify your email and phone number to complete registration</p>
      </div>

      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{errors.general}</p>
        </div>
      )}

      {/* CAPTCHA for OTP sending */}
      {(!verificationState.emailVerified || !verificationState.phoneVerified) && (
        <Captcha
          value={captchaInputStep2}
          onChange={(e) => {
            setCaptchaInputStep2(e.target.value);
            if (errors.general && errors.general.includes('CAPTCHA')) {
              setErrors(prev => ({ ...prev, general: '' }));
            }
          }}
          error={errors.general && errors.general.includes('CAPTCHA') ? errors.general : ''}
          disabled={isSubmitting}
          onCaptchaChange={(data) => setCaptchaKeyStep2(data.key)}
          refreshTrigger={refreshTriggerStep2}
        />
      )}

      {/* Email Verification */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${verificationState.emailVerified ? 'bg-green-100' : 'bg-gray-200'
              }`}>
              {verificationState.emailVerified ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <span className="text-sm font-medium text-gray-600">1</span>
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">Email Verification</p>
              <p className="text-sm text-gray-600">{obfuscateEmail(registrationData.email)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {verificationState.emailVerified ? (
              <span className="text-sm text-green-600 font-medium">Verified</span>
            ) : (
              <button
                onClick={() => handleSendOtp('email')}
                disabled={isSubmitting}
                className={`px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-700'
                  }`}
              >
                Send OTP
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Phone Verification */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${verificationState.phoneVerified ? 'bg-green-100' : 'bg-gray-200'
              }`}>
              {verificationState.phoneVerified ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <span className="text-sm font-medium text-gray-600">2</span>
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">Phone Verification</p>
              <p className="text-sm text-gray-600">{registrationData.mobile}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {verificationState.phoneVerified ? (
              <span className="text-sm text-green-600 font-medium">Verified</span>
            ) : (
              <button
                onClick={() => handleSendOtp('phone')}
                disabled={isSubmitting}
                className={`px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-700'
                  }`}
              >
                Send OTP
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Back Button */}
      <button
        type="button"
        onClick={() => {
          setCurrentStep(1);
          setRefreshTriggerStep1(prev => prev + 1);
          setCaptchaInput('');
        }}
        className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold transition-colors duration-300 hover:bg-gray-50 flex items-center justify-center space-x-2"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back</span>
      </button>
    </form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Back to Login Link */}
        <div className="text-left">
          <button
            type="button"
            onClick={() => navigate('/vigilance-login')}
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300 inline-flex items-center space-x-2"
            aria-label="Back to login"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Back to Login</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="SJVN Limited Logo"
            className="w-18 h-24 mx-auto mb-4"
            role="img"
            aria-label="SJVN Limited Logo"
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Vigilance Signup</h2>
          <p className="text-gray-600">Create your vigilance account</p>
          <div className="w-24 h-1 bg-primary-600 mx-auto mt-4"></div>
        </div>


        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
        </div>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/vigilance-login')}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Login here
            </button>
          </p>
        </div>
      </div>

      {/* Verification Dialog */}
      <VerificationDialog
        isOpen={verificationDialog.isOpen}
        onClose={() => setVerificationDialog({ isOpen: false, type: '', login: '' })}
        verificationType={verificationDialog.type}
        loginValue={verificationDialog.login}
        onVerify={handleVerifyOtp}
        isVerifying={isSubmitting}
        error={errors.general}
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'success' })}
      />
    </div>
  );
};

export default VigilanceSignup;